import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { useGuild } from './GuildLayout.jsx';

const GROUPS = [
  {
    key: 'member',
    title: 'Member events',
    desc: 'Per-member changes — joining, leaving, nickname, roles, profile, boosts.',
    channelKey: 'log_channel_member',
    toggles: [
      { id: 'member_join',         label: 'Member joined' },
      { id: 'member_leave',        label: 'Member left' },
      { id: 'nickname_change',     label: 'Nickname changed' },
      { id: 'role_change',         label: 'Roles added or removed' },
      { id: 'username_change',     label: 'Username changed' },
      { id: 'display_name_change', label: 'Display name changed' },
      { id: 'avatar_change',       label: 'Avatar changed' },
      { id: 'boost_start',         label: 'Started boosting server' },
      { id: 'boost_end',           label: 'Stopped boosting server' },
      { id: 'invite_create',       label: 'Invite created' },
      { id: 'invite_delete',       label: 'Invite deleted' },
    ],
  },
  {
    key: 'message',
    title: 'Message events',
    desc: 'Edits, deletions, and bulk deletions in any channel.',
    channelKey: 'log_channel_message',
    toggles: [
      { id: 'message_delete',      label: 'Message deleted' },
      { id: 'message_edit',        label: 'Message edited' },
      { id: 'message_bulk_delete', label: 'Bulk delete (purge)' },
      { id: 'message_pin',         label: 'Message pinned' },
      { id: 'message_unpin',       label: 'Message unpinned' },
      { id: 'reaction_remove_all', label: 'All reactions cleared' },
    ],
  },
  {
    key: 'mod',
    title: 'Moderation actions',
    desc: 'Every action taken via SoupOS or Discord native — warns, timeouts, kicks, bans, unbans, purges.',
    channelKey: 'log_channel_mod',
    toggles: [
      { id: 'mod_action', label: 'All moderation actions' },
    ],
  },
  {
    key: 'voice',
    title: 'Voice events',
    desc: 'Voice channel join/leave/move, server-mute/deafen, screen-share and camera transitions.',
    channelKey: 'log_channel_voice',
    toggles: [
      { id: 'voice_join',             label: 'Joined voice channel' },
      { id: 'voice_leave',            label: 'Left voice channel' },
      { id: 'voice_move',             label: 'Moved between voice channels' },
      { id: 'voice_server_mute',      label: 'Server-muted' },
      { id: 'voice_server_unmute',    label: 'Server-unmuted' },
      { id: 'voice_server_deafen',    label: 'Server-deafened' },
      { id: 'voice_server_undeafen',  label: 'Server-undeafened' },
      { id: 'voice_stream_start',     label: 'Started streaming' },
      { id: 'voice_stream_stop',      label: 'Stopped streaming' },
      { id: 'voice_video_start',      label: 'Turned camera on' },
      { id: 'voice_video_stop',       label: 'Turned camera off' },
    ],
  },
  {
    key: 'server',
    title: 'Server events',
    desc: 'Channel, role, emoji, sticker, scheduled-event, and server-wide changes.',
    channelKey: 'log_channel_server',
    toggles: [
      { id: 'channel_create',   label: 'Channel created' },
      { id: 'channel_delete',   label: 'Channel deleted' },
      { id: 'channel_update',   label: 'Channel updated' },
      { id: 'guild_update',     label: 'Server settings updated' },
      { id: 'role_create',      label: 'Role created' },
      { id: 'role_delete',      label: 'Role deleted' },
      { id: 'role_update',      label: 'Role updated (name/color/perms)' },
      { id: 'emoji_create',     label: 'Emoji added' },
      { id: 'emoji_delete',     label: 'Emoji removed' },
      { id: 'emoji_update',     label: 'Emoji renamed' },
      { id: 'sticker_create',   label: 'Sticker added' },
      { id: 'sticker_delete',   label: 'Sticker removed' },
      { id: 'sticker_update',   label: 'Sticker updated' },
      { id: 'event_create',     label: 'Scheduled event created' },
      { id: 'event_delete',     label: 'Scheduled event cancelled' },
      { id: 'event_update',     label: 'Scheduled event updated' },
      { id: 'thread_create',    label: 'Thread created' },
      { id: 'thread_delete',    label: 'Thread deleted' },
      { id: 'thread_update',    label: 'Thread updated' },
    ],
  },
];

const DEFAULTS = GROUPS.flatMap(g => g.toggles.map(t => t.id));

export default function ConfigLogs() {
  const { guildId, channels } = useGuild();
  const [cfg, setCfg] = useState(null);
  const [enabled, setEnabled] = useState(new Set(DEFAULTS));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    api.getConfig(guildId).then(c => {
      setCfg({
        log_channel_id:      c.log_channel_id      ?? null,
        log_channel_member:  c.log_channel_member  ?? null,
        log_channel_message: c.log_channel_message ?? null,
        log_channel_mod:     c.log_channel_mod     ?? null,
        log_channel_server:  c.log_channel_server  ?? null,
        log_channel_voice:   c.log_channel_voice   ?? null,
      });
      if (c.log_events) {
        try { setEnabled(new Set(JSON.parse(c.log_events))); }
        catch { setEnabled(new Set(DEFAULTS)); }
      } else {
        setEnabled(new Set(DEFAULTS));
      }
      setDirty(false);
    });
  }, [guildId]);

  if (!cfg) return (
    <div className="card">
      <div className="skel skel-line med" />
      <div className="skel" style={{ height: 38, marginTop: 12 }} />
    </div>
  );

  function updateChannel(key, value) {
    setCfg(c => ({ ...c, [key]: value || null }));
    setDirty(true);
  }

  function toggleEvent(id) {
    setEnabled(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setDirty(true);
  }

  function toggleGroup(group, on) {
    setEnabled(prev => {
      const next = new Set(prev);
      for (const t of group.toggles) {
        if (on) next.add(t.id); else next.delete(t.id);
      }
      return next;
    });
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    try {
      await api.putConfig(guildId, { ...cfg, log_events: JSON.stringify([...enabled]) });
      setDirty(false);
      setToast({ type: 'success', msg: 'Saved' });
    } catch (err) {
      setToast({ type: 'error', msg: err.message });
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 2500);
    }
  }

  return (
    <>
      <h2 style={{ marginTop: 0 }}>Logging</h2>
      <p className="muted" style={{ marginTop: '-.25rem' }}>Route different event categories to different channels. Toggle individual events on or off.</p>

      {GROUPS.map(group => {
        const allOn = group.toggles.every(t => enabled.has(t.id));
        const someOn = group.toggles.some(t => enabled.has(t.id));
        return (
          <div className="card" key={group.key}>
            <div className="row" style={{ alignItems: 'flex-start', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ margin: 0 }}>{group.title}</h3>
                <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>{group.desc}</p>
              </div>
              <div style={{ flex: 0 }}>
                <button className="ghost" onClick={() => toggleGroup(group, !allOn)}>
                  {allOn ? 'Disable all' : someOn ? 'Enable all' : 'Enable all'}
                </button>
              </div>
            </div>

            <div className="field">
              <label>Destination channel</label>
              <select value={cfg[group.channelKey] || ''} onChange={e => updateChannel(group.channelKey, e.target.value)}>
                <option value="">— disabled —</option>
                {channels.filter(c => c.type === 0 || c.type === 5).map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
              </select>
            </div>

            <div className="event-grid">
              {group.toggles.map(t => (
                <label key={t.id} className="event-toggle">
                  <input
                    type="checkbox"
                    checked={enabled.has(t.id)}
                    onChange={() => toggleEvent(t.id)}
                  />
                  <span>{t.label}</span>
                </label>
              ))}
            </div>
          </div>
        );
      })}

      <div className="row actions">
        <button disabled={!dirty || saving} onClick={save}>{saving ? 'Saving…' : 'Save changes'}</button>
      </div>

      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}
